import React, { useState, useMemo } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { PageHeader, PageBody, StatTile, EmptyState } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const INCOME_CATEGORIES = ["Invoice Payment", "Deposit", "Service Call", "Other Income"];
const EXPENSE_CATEGORIES = ["Materials", "Labor", "Fuel", "Equipment", "Insurance", "Marketing", "Subcontractor", "Disposal", "Office", "Other"];

export default function Accounting() {
  const { data: items = [] } = useSWR("/ledger", fetcher);
  const { data: summary } = useSWR("/accounting/summary", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0,10), type: "expense", category: "Materials", description: "", amount: 0,
  });

  const submit = async () => {
    if (!form.description || !Number(form.amount)) return toast.error("Description & amount required");
    try {
      await api.post("/ledger", { ...form, amount: Number(form.amount) });
      toast.success("Posted");
      setOpen(false);
      setForm({ ...form, description: "", amount: 0 });
      mutate("/ledger"); mutate("/accounting/summary");
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/ledger/${id}`);
    mutate("/ledger"); mutate("/accounting/summary");
  };

  const cats = form.type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  const chartData = useMemo(() => {
    const m = {};
    items.forEach(i => {
      const ym = (i.date || "").slice(0, 7);
      if (!m[ym]) m[ym] = { month: ym, income: 0, expense: 0 };
      m[ym][i.type] += Number(i.amount || 0);
    });
    return Object.values(m).sort((a, b) => a.month.localeCompare(b.month)).slice(-6);
  }, [items]);

  return (
    <div data-testid="accounting-page">
      <PageHeader
        eyebrow="// 08 / Accounting"
        title="The Books"
        sub="Income, expenses, P&L. Stripe-paid invoices auto-post here."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-ledger-button" className="rounded-none bg-zinc-900 hover:bg-orange-600 text-white uppercase font-bold tracking-wider text-xs h-11 px-5">
                <Plus size={14} className="mr-2" /> New Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-zinc-300 max-w-md">
              <DialogHeader><DialogTitle className="font-display font-black text-2xl tracking-tight">New Entry</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Type">
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, category: v === "income" ? "Invoice Payment" : "Materials" })}>
                      <SelectTrigger data-testid="led-type" className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="income">Income</SelectItem>
                        <SelectItem value="expense">Expense</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Date"><Input data-testid="led-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-none mono" /></Field>
                </div>
                <Field label="Category">
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="led-category" className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>{cats.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Description"><Input data-testid="led-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none" /></Field>
                <Field label="Amount ($)"><Input data-testid="led-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-none mono" /></Field>
                <Button data-testid="led-save" onClick={submit} className="rounded-none bg-orange-600 hover:bg-orange-700 text-white uppercase font-bold tracking-wider w-full h-11">Post entry</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <PageBody>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 mb-8">
          <StatTile testId="acc-income" label="Income" value={fmt$(summary?.income)} hint="ALL TIME" />
          <StatTile testId="acc-expense" label="Expenses" value={fmt$(summary?.expense)} hint="ALL TIME" />
          <StatTile testId="acc-profit" label="Net Profit" value={fmt$(summary?.profit)} hint="REVENUE − COSTS" accent />
        </div>

        {chartData.length > 0 && (
          <div className="border border-zinc-200 mb-8">
            <div className="border-b border-zinc-200 px-5 py-3"><p className="label-eyebrow text-zinc-500">P&L · Last 6 months</p></div>
            <div className="p-5 h-72">
              <ResponsiveContainer>
                <BarChart data={chartData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="month" tick={{ fontFamily: 'IBM Plex Mono', fontSize: 11 }} stroke="#52525b" />
                  <YAxis tick={{ fontFamily: 'IBM Plex Mono', fontSize: 11 }} stroke="#52525b" />
                  <Tooltip contentStyle={{ border: '1px solid #e4e4e7', borderRadius: 0, fontFamily: 'IBM Plex Mono', fontSize: 12 }} />
                  <Bar dataKey="income" fill="#16A34A" />
                  <Bar dataKey="expense" fill="#EA580C" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {items.length === 0 ? (
          <EmptyState title="Ledger is empty" sub="Post your first entry or get paid via Stripe — it logs automatically." />
        ) : (
          <div className="border border-zinc-200">
            <div className="grid grid-cols-12 px-5 py-3 border-b border-zinc-200 label-eyebrow text-zinc-500 bg-zinc-50">
              <div className="col-span-2">Date</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-3">Category</div>
              <div className="col-span-3">Description</div>
              <div className="col-span-1 text-right">Amount</div>
              <div className="col-span-1"></div>
            </div>
            {items.map(e => (
              <div key={e.id} className="grid grid-cols-12 px-5 py-3 border-b border-zinc-100 items-center text-sm">
                <div className="col-span-2 mono text-xs">{e.date}</div>
                <div className="col-span-2">
                  <span className={`label-eyebrow px-2 py-0.5 border ${e.type === "income" ? "border-green-300 text-green-700 bg-green-50" : "border-orange-300 text-orange-700 bg-orange-50"}`}>
                    {e.type}
                  </span>
                </div>
                <div className="col-span-3 text-zinc-700">{e.category}</div>
                <div className="col-span-3 truncate">{e.description}</div>
                <div className={`col-span-1 text-right mono font-semibold ${e.type === "income" ? "text-green-700" : "text-zinc-900"}`}>
                  {e.type === "income" ? "+" : "−"}{fmt$(e.amount)}
                </div>
                <div className="col-span-1 text-right">
                  <button data-testid={`led-delete-${e.id}`} onClick={() => remove(e.id)} className="text-zinc-400 hover:text-orange-600"><Trash size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </PageBody>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-eyebrow text-zinc-600 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
